import axios from "axios";
import { TokenService } from "../services/storage.service";
import general from "./../utils/general";
import store from "../store";
import posthog from "posthog-js";

const localAssetOrigins = () => {
	const origins = ["http://localhost:3002", "https://localhost:3002", "http://127.0.0.1:3002", "https://127.0.0.1:3002"];

	if (process.env.VUE_APP_PROXY_TARGET) {
		origins.push(process.env.VUE_APP_PROXY_TARGET.replace(/\/$/, ""));
	}

	return origins;
};

const normalizeLocalAssetUrl = (value) => {
	if (process.env.NODE_ENV !== "development" || typeof value !== "string") return value;

	for (const origin of localAssetOrigins()) {
		if (value.startsWith(origin + "/images/")) {
			return value.slice(origin.length);
		}
	}

	return value;
};

const normalizeLocalAssetUrls = (value) => {
	const normalized = normalizeLocalAssetUrl(value);
	if (normalized !== value) return normalized;

	if (Array.isArray(value)) {
		return value.map(normalizeLocalAssetUrls);
	}

	if (value && typeof value === "object") {
		Object.keys(value).forEach((key) => {
			value[key] = normalizeLocalAssetUrls(value[key]);
		});
	}

	return value;
};

const ApiService = {
	init(baseURL) {
		axios.defaults.baseURL = baseURL;
		axios.defaults.withCredentials = true;

		const transformResponse = axios.defaults.transformResponse;
		const transforms = Array.isArray(transformResponse) ? transformResponse : transformResponse ? [transformResponse] : [];
		if (!transforms.includes(normalizeLocalAssetUrls)) {
			axios.defaults.transformResponse = transforms.concat(normalizeLocalAssetUrls);
		}
	},

	_requestInterceptor: null,
	_responseInterceptor: null,

	setHeader() {
		const token = TokenService.getToken();
		if (token) {
			axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
		}
		axios.defaults.headers.common["TimezoneOffset"] = new Date().getTimezoneOffset() / 60;

		if (general.isDev()) {
			const extraInfo = TokenService.getExtraInfo();

			if (extraInfo && extraInfo.admin) {
				axios.defaults.headers.common["admin"] = extraInfo.admin;
			}
		}

		// Note: version variable not defined - commenting out for now
		// if ( typeof version !== 'undefined' && version ) {
		//     axios.defaults.headers.common["version"] = version;
		// }

		const postHogId = posthog.get_distinct_id();

		if (postHogId) {
			axios.defaults.headers.common["posthog"] = postHogId;
		}

		const brandId = TokenService.getBrand();

		if (brandId) {
			axios.defaults.headers.common["brand"] = brandId;
		}
	},

	removeHeader() {
		axios.defaults.headers.common = {};
	},

	get(resource, data) {
		return axios.get(resource, data);
	},

	post(resource, data, ops) {
		return axios.post(resource, data, ops);
	},

	put(resource, data) {
		return axios.put(resource, data);
	},

	delete(resource, data) {
		return axios.delete(resource, { data });
	},

	customRequest(data) {
		return axios(data);
	},

	mountInterceptor() {
		if (this._requestInterceptor !== null || this._responseInterceptor !== null) {
			return;
		}

		const forceSessionExpiredLogout = () => {
			if (!TokenService.getToken()) return;
			store.commit("general/updateLoadingText", "");
			store.dispatch("auth/logoutForce", { reason: "session-expired" });
		};

		this._requestInterceptor = axios.interceptors.request.use((config) => {
			const brandId = TokenService.getBrand();
			if (brandId) {
				config.headers = config.headers || {};
				config.headers.brand = brandId;
			}
			return config;
		});

		this._responseInterceptor = axios.interceptors.response.use(
			(response) => {
				if (typeof response.data === "object") {
					if (typeof response.data.globalMessage !== "undefined") {
						store.commit("general/printGlobalMessage", response.data.globalMessage);
					}
					if (typeof response.data.posthogEvent !== "undefined") {
						general.posthogCapture(response.data.posthogEvent.name, response.data.posthogEvent.properties || {});
					}
				}
				return response;
			},
			async (error) => {
				const status = error?.response?.status;
				const requestConfig = error?.config || {};
				const requestUrl = requestConfig.url || "";

				if (status == 401) {
					if (requestUrl.includes("users/refresh-token")) {
						// Refresh token has failed. Logout the user
						forceSessionExpiredLogout();
						throw error;
					} else {
						// Refresh the access token
						try {
							await store.dispatch("auth/refreshToken");
							store.commit("general/updateLoadingText", "");
							// Retry the original request

							let data = requestConfig.data,
								data2;

							try {
								data2 = JSON.parse(data);
							} catch (err) {
								data2 = data;
							}

							return this.customRequest({
								method: requestConfig.method,
								url: requestUrl,
								data: data2,
							});
						} catch (e) {
							// Refresh has failed - reject the original request
							forceSessionExpiredLogout();
							throw error;
						}
					}
				}

				if (status == 403) {
					store.commit("general/updateLoadingText", "");
					store.dispatch("auth/logoutForce", error.response.data || null);

					throw error;
				}

				// If error was not 401 just reject as is
				throw error;
			}
		);
	},

	unmountInterceptor() {
		if (this._requestInterceptor !== null) {
			axios.interceptors.request.eject(this._requestInterceptor);
			this._requestInterceptor = null;
		}
		if (this._responseInterceptor !== null) {
			axios.interceptors.response.eject(this._responseInterceptor);
			this._responseInterceptor = null;
		}
	},
};

export default ApiService;
